import { computed, ref, type Ref } from 'vue';

import type { SearchComboboxOption } from '@/shared/ui';

export const PROTHEUS_RECIPIENT_SEARCH_LIMIT = 20;

export interface ProtheusRecipientOption {
  id: string;
  name: string;
}

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();

export function useProtheusRecipientSearch(
  recipients: Readonly<Ref<readonly ProtheusRecipientOption[]>>,
  selectedId: Readonly<Ref<string>>,
  notFoundMessage: string
) {
  const searchTerm = ref('');

  const normalizedRecipients = computed(() =>
    [...recipients.value]
      .sort((first, second) => first.name.localeCompare(second.name, 'pt-BR'))
      .map((recipient) => ({
        recipient,
        searchText: normalizeSearchText(recipient.name),
      }))
  );

  const options = computed<SearchComboboxOption[]>(() => {
    const term = normalizeSearchText(searchTerm.value);
    const matches =
      term === ''
        ? normalizedRecipients.value
        : normalizedRecipients.value.filter(({ searchText }) => searchText.includes(term));

    return matches.slice(0, PROTHEUS_RECIPIENT_SEARCH_LIMIT).map(({ recipient }) => ({
      value: recipient.id,
      label: recipient.name,
    }));
  });

  const emptyMessage = computed(() => (searchTerm.value.trim() ? notFoundMessage : ''));

  const selectedLabel = computed(
    () => recipients.value.find((recipient) => recipient.id === selectedId.value)?.name
  );

  const selectedValue = computed(() => selectedId.value);

  const search = (term: string) => {
    searchTerm.value = term;
  };

  return { options, emptyMessage, selectedLabel, selectedValue, search };
}
